import { InitialAllocationDataStore } from '@modules/showroom/EnhancedOrder/stores/EnhancedListView/InitialAllocationDataStore'
import { ListTableView } from '@modules/order/stores/view/List/ListViewTableService'
import { EnhancedListUIStore, ISize } from './EnhancedListUIStore'
import InitialAllocationListViewTableService from './InitialAllocationListViewTableService'
import { nav } from '@stores'
import { getEnumValue } from '@utils/get-enum-value'
import { VIPClientType } from '@modules/common/models/enums/VIPClientType'
import { action } from 'mobx'
import { filter, find, isEmpty, sum, sumBy, unionBy } from 'lodash'
import { METRIC_ATTRIBUTES_FOR_IA_RETAIL_QUANTITY } from '@modules/order/constants/uiConstants'

export class InitialAllocationUIStore<
  T extends InitialAllocationDataStore
> extends EnhancedListUIStore<T> {
  tableService = new InitialAllocationListViewTableService(
    ListTableView.Quantity,
    nav.queryParams.clientType === getEnumValue(VIPClientType.Internal)
      ? [ListTableView.Summary, ListTableView.Quantity]
      : [ListTableView.Quantity],
    {}
  )

  getSizes = (productId: string): Array<ISize> => {
    const IAProduct = this.dataStore.getIAProductData(productId) || {}
    const product = this.dataStore.itemIndexObjectStore.idObjectMap.get(productId)
    const combinedInactiveSizes = unionBy(
      IAProduct?.inactiveSizes,
      product?.inactiveSizes,
      'id'
    )
    const sizes = (
      IAProduct?.['original-sizes-id']?.reduce((acc, size, index) => {
        acc?.push({
          id: size,
          name: IAProduct?.['original-sizes-name'][index],
          sizeCode: IAProduct?.['original-sizes-sizeCode'][index]
        })
        return acc
      }, []) || []
    )
      .concat(
        combinedInactiveSizes.map(inactiveSize => {
          return { ...inactiveSize, isInactive: true }
        })
      )
      .sort((sizeA, sizeB) => sizeA.sizeCode - sizeB.sizeCode)
    return sizes
  }

  getIAWarehouseSizeCurve = (productId: string) => {
    const sizeCurves = { ...(this.getProductCurves(productId).sizeCurve ?? {}) }
    if (!isEmpty(sizeCurves?.curves)) {
      const sizes = this.getSizes(productId)
      const calculatedSizeCurves = sizes?.reduce(
        (acc, { name, isInactive }) => {
          return {
            ...acc,
            curve: {
              ...(acc?.curve || {}),
              ...(sizeCurves?.curves?.[name]
                ? isInactive
                  ? {}
                  : {
                      [name]: sizeCurves.curves?.[name]
                    }
                : {})
            }
          }
        },
        { curve: {} }
      )
      const productCurve = calculatedSizeCurves.curve || {}
      const curveTotal = sum(Object.values(productCurve))
      if (curveTotal) {
        return Object.keys(productCurve).reduce(
          (acc, key) => ({ ...acc, [key]: productCurve[key] / curveTotal }),
          {}
        )
      }
    }
    return {}
  }

  getIAWarehouseQuantitiesBySizeCurve = (
    inputValue: number,
    sizes: any[],
    curves: any,
    isCaptureSizeEnabled: boolean
  ) => {
    let sizeQuantites = {}
    sizes.forEach(size => {
      const curve = curves[size.name]
      if (curve) {
        sizeQuantites[size.id] = this.getQuantityByCurve(inputValue, curve)
      } else {
        sizeQuantites[size.id] = 0
      }
    }, {})
    const hasNoSizesData = !isCaptureSizeEnabled || !sizes.length
    return {
      warehouseSizeQuantity: hasNoSizesData
        ? inputValue
        : sum(Object.values(sizeQuantites)),
      warehouseSizeQuantities: sizeQuantites
    }
  }

  handleIADoorLevelQuantitytyUpdate = (
    productId: string,
    doorId: string,
    newValue: number,
    doorOrderId?: string,
    isCaptureSizeEnabled?: boolean
  ) => {
    const product = this.dataStore.itemIndexObjectStore.idObjectMap.get(productId)
    const IAProduct = this.dataStore.getIAProductData(productId) || {}

    const { doorOrderProducts = {} } = IAProduct

    const sizes = this.getSizes(productId)
    const sizeQuantities = this.getQuantitiesBySizeCurve(
      newValue,
      sizes,
      this.getProductSizeCurve(productId, doorId),
      doorOrderProducts[doorId]?.deliverySizeQuantities || {}
    )
    const updatedSize = {
      doorOrderId,
      draftQuantity: newValue,
      sizeQuantity:
        isEmpty(sizeQuantities) || !isCaptureSizeEnabled
          ? newValue
          : sum(Object.values(sizeQuantities)),
      sizeQuantities
    }

    let updatedIAProduct: any = {}
    let updatedSizeQuantity = {}

    Object.keys(sizeQuantities).forEach(sizeId => {
      updatedSizeQuantity[sizeId] = {
        sizeId,
        quantity: sizeQuantities?.[sizeId]
      }
    })

    updatedIAProduct = { ...IAProduct }
    updatedIAProduct.doorOrderProducts[doorId] = {
      ...(updatedIAProduct.doorOrderProducts[doorId] || {}),
      deliveryDoorQuantity: newValue,
      deliverySizeQuantity: updatedSize?.sizeQuantity || 0,
      deliverySizeQuantities: updatedSizeQuantity || {}
    }

    const payload = {
      isDoorOrder: false,
      ...updatedSize,
      aggregations: {
        heirarchy: {
          ...this.dataStore.getPayloadForUpdatedPageLevelHeirarchy(product),
          metricAttributes: ['totalDeliveryDoorQuantity']
        },
        totalDoorHierarchyKPIs: {
          ...this.dataStore.getPayloadForUpdatedDoorLevelHeirarchy(),
          metricAttributes: ['totalDeliveryDoorQuantity']
        },
        ...this.dataStore.getPayloadForSingleProductAggregation(productId, [
          'totalDeliverySizeQuantity',
          'totalDeliveryDoorQuantity'
        ])
      }
    }

    this.dataStore.updateDoorLevelTotalQuantity(productId, payload, updatedIAProduct)
  }

  @action
  updateIASizeQuantity = (
    productId: string,
    doorOrderId: string,
    doorId: string,
    newValue: number,
    sizeId: string
  ) => {
    const IAProduct = this.dataStore.getIAProductData(productId)
    const newDeliverySizeQuantities =
      IAProduct?.doorOrderProducts[doorId]?.deliverySizeQuantities || {}
    newDeliverySizeQuantities[sizeId] =
      {
        sizeId,
        quantity: newValue
      } || {}
    const totalDeliverySizeQuantity = sumBy(
      Object.values(newDeliverySizeQuantities),
      'quantity'
    )
    const updatedIAProduct = IAProduct
    updatedIAProduct.doorOrderProducts[doorId].deliverySizeQuantity =
      totalDeliverySizeQuantity || 0
    updatedIAProduct.doorOrderProducts[doorId].deliverySizeQuantities =
      newDeliverySizeQuantities

    const payload = {
      sizeQuantity: newValue,
      isDoorOrder: false,
      aggregations: {
        ...this.dataStore.getPayloadForSingleProductAggregation(productId, [
          'totalDeliverySizeQuantity'
        ])
      }
    }
    return this.dataStore.updateIASizeQuantity(
      productId,
      payload,
      doorOrderId,
      sizeId,
      updatedIAProduct
    )
  }

  @action
  updateDeliveryAllocatedProductQuantity = (productId, value) => {
    const payload = {
      deliveryAllocatedProductQuantity: value
    }
    return this.dataStore.updateDeliveryAllocatedProductQuantity(productId, payload)
  }

  @action
  updateProductPercentToggleState = (
    flag: boolean,
    percentVal: Number,
    productId: string
  ) => {
    this.dataStore.updateProductPercentToggleState(flag, percentVal, productId)
  }

  updateIAProductQuantity = (productId: string, newIAProductQty: number) => {
    this.dataStore.setIAProductQuantityCache(newIAProductQty)
    const product = this.dataStore.itemIndexObjectStore.idObjectMap.get(productId)
    const payload = {
      deliveryProductQuantity: newIAProductQty,
      aggregations: {
        heirarchy: {
          metricAttributes: ['totalDeliveryProductQuantity'],
          ...this.dataStore.getPayloadForUpdatedPageLevelHeirarchy(product)
        }
      }
    }
    return this.dataStore.updateIAProductQuantity(productId, payload)
  }

  @action
  bulkUpdateDeliveryDoorOrderProducts = (summary, productId, value) => {
    const IAProduct = this.dataStore.getIAProductData(productId)
    const { doorOrderProducts = {}, deliveryProductQuantity } = IAProduct
    const doorIds = Object.keys(doorOrderProducts)
    const activeDoorIds = filter(
      doorIds,
      doorId =>
        !find(summary?.doorOrders, doorOrder => doorOrder?.door?.id === doorId)?.deleted
    )
    const productDoorCurves = this.getProductDoorCurveByGrading(productId, activeDoorIds)
    const doorQuantitiesByCurve = this.getDoorQuantitiesByDoorCurve(
      value,
      doorOrderProducts,
      productDoorCurves,
      'deliveryDoorQuantity'
    )
    const sizes = this.getSizes(productId)
    const isCaptureSizeEnabled =
      summary?.client?.captureSize || summary?.clientOrder?.client?.captureSize

    const IAProductQuantityCache =
      this.dataStore.IAProductQuantityCache ?? deliveryProductQuantity
    const calculatedRemainingProductQuantity =
      IAProductQuantityCache && IAProductQuantityCache >= value
        ? IAProductQuantityCache - value
        : 0

    const warehouseQuantityPayload = this.getIAWarehouseQuantitiesBySizeCurve(
      calculatedRemainingProductQuantity,
      sizes,
      this.getIAWarehouseSizeCurve(productId),
      isCaptureSizeEnabled
    )
    const bulkDoorOrderQuantities = activeDoorIds.map(doorId => {
      const sizeQuantities = this.getQuantitiesBySizeCurve(
        doorQuantitiesByCurve[doorId] ?? 0,
        sizes,
        this.getProductSizeCurve(productId, doorOrderProducts[doorId].doorId),
        doorOrderProducts[doorId].deliverySizeQuantities || {}
      )
      return {
        doorOrderId: find(
          summary?.doorOrders,
          doorOrder => doorOrder?.door?.id === doorId
        )?.id,
        draftQuantity: doorQuantitiesByCurve[doorId] ?? 0,
        sizeQuantity:
          isEmpty(sizeQuantities) || !isCaptureSizeEnabled
            ? doorQuantitiesByCurve[doorId] ?? 0
            : sum(Object.values(sizeQuantities)),
        sizeQuantities
      }
    })
    const payload = {
      bulkDoorOrderQuantities,
      ...warehouseQuantityPayload,
      aggregations: {
        heirarchy: {
          metricAttributes: METRIC_ATTRIBUTES_FOR_IA_RETAIL_QUANTITY,
          ...this.dataStore.getPayloadForUpdatedPageLevelHeirarchy(IAProduct)
        },
        totalDoorHierarchyKPIs: {
          metricAttributes: METRIC_ATTRIBUTES_FOR_IA_RETAIL_QUANTITY,
          ...this.dataStore.getPayloadForUpdatedDoorLevelHeirarchy()
        }
      }
    }

    return this.dataStore.bulkUpdateDeliveryDoorOrderProducts(summary, productId, payload)
  }

  rollUpIAProductQuantity = productId => {
    const product = this.dataStore.itemIndexObjectStore.idObjectMap.get(productId)
    const payload = {
      // deliveryProductQuantity: newIAProductQty,
      aggregations: {
        heirarchy: {
          ...this.dataStore.getPayloadForUpdatedPageLevelHeirarchy(product),
          metricAttributes: ['totalDeliveryProductQuantity', 'totalDeliveryDoorQuantity']
        },
        totalDoorHierarchyKPIs: {
          ...this.dataStore.getPayloadForUpdatedDoorLevelHeirarchy(),
          metricAttributes: ['totalDeliveryDoorQuantity']
        }
      }
    }
    return this.dataStore.rollUpIAProductQuantity(productId, payload)
  }
}
